import type { ApiSwatidRelay } from '@/types/api';

type RelayRaw = Record<string, unknown>;

const DEFAULT_RELAY_NAME_PATTERN = /^Rel[eé]?\s*(\d{1,2})$/i;

/** Normaliza respuesta API (camelCase o snake_case) */
export function normalizeSwatidRelay(raw: RelayRaw): ApiSwatidRelay {
  const relayNumber = Number(
    raw.relayNumber ?? raw.relay_number ?? raw.relay ?? raw.number ?? 0
  );
  const name = String(
    raw.name ?? raw.relayName ?? raw.relay_name ?? raw.label ?? ''
  ).trim();

  const modeRaw = String(raw.mode ?? 'temporizado');
  const mode: ApiSwatidRelay['mode'] =
    modeRaw === 'fijo' ? 'fijo' : 'temporizado';

  return {
    relayNumber,
    name: name || `Relé ${relayNumber}`,
    mode,
    defaultDuration: Number(raw.defaultDuration ?? raw.default_duration ?? 300) || 300,
    currentState: Boolean(raw.currentState ?? raw.current_state ?? false),
    timerRemaining: Number(raw.timerRemaining ?? raw.timer_remaining ?? 0) || 0,
  };
}

export function normalizeSwatidRelays(data: unknown): ApiSwatidRelay[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => normalizeSwatidRelay(item as RelayRaw))
    .filter((r) => r.relayNumber >= 1 && r.relayNumber <= 16)
    .sort((a, b) => a.relayNumber - b.relayNumber);
}

/** El backend devuelve "Relé N" como nombre por defecto hasta que el usuario personaliza */
export function isDefaultSwatidRelayName(
  relayNumber: number,
  name?: string | null
): boolean {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) return true;
  const match = trimmed.match(DEFAULT_RELAY_NAME_PATTERN);
  if (match) return Number(match[1]) === relayNumber;
  return trimmed === `Relé ${relayNumber}`;
}

export function isCustomSwatidRelayName(
  relay: Pick<ApiSwatidRelay, 'relayNumber' | 'name'>
): boolean {
  return !isDefaultSwatidRelayName(relay.relayNumber, relay.name);
}

export function getSwatidRelayDisplayName(
  relay: Pick<ApiSwatidRelay, 'relayNumber' | 'name'>
): string {
  const trimmed = relay.name?.trim();
  return trimmed || `Relé ${relay.relayNumber}`;
}

/** Etiqueta para selectores: "Relé 3" o "Relé 3 - Zona Norte" */
export function getSwatidRelayOptionLabel(
  relay: Pick<ApiSwatidRelay, 'relayNumber' | 'name'>
): string {
  const base = `Relé ${relay.relayNumber}`;
  if (isCustomSwatidRelayName(relay)) {
    return `${base} — ${relay.name.trim()}`;
  }
  return base;
}

export function validateSwatidRelayName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'El nombre del relé es obligatorio';
  if (trimmed.length > 100) return 'El nombre no puede superar 100 caracteres';
  return null;
}

export function buildRelayConfigPayload(params: {
  name?: string;
  mode?: ApiSwatidRelay['mode'];
  defaultDuration?: number;
}): { name?: string; mode?: ApiSwatidRelay['mode']; defaultDuration?: number } {
  const payload: {
    name?: string;
    mode?: ApiSwatidRelay['mode'];
    defaultDuration?: number;
  } = {};

  if (params.name !== undefined) {
    payload.name = params.name.trim();
  }
  if (params.mode !== undefined) {
    payload.mode = params.mode;
  }
  if (params.defaultDuration !== undefined && params.defaultDuration > 0) {
    payload.defaultDuration = Math.round(params.defaultDuration);
  }

  return payload;
}
